import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Box, Flex, Grid, Text } from '@chakra-ui/react'
import { AnimatePresence, motion } from 'framer-motion'
import { DatePicker } from '@minetrace/ui/forms'
import {
  ComposedChart,
  Line,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceArea,
  ReferenceLine,
} from 'recharts'
import type { IconType } from 'react-icons'
import {
  LuActivity,
  LuRefreshCw,
  LuDownload,
  LuFileText,
  LuFileSpreadsheet,
  LuShieldCheck,
  LuFolderPlus,
  LuSettings,
  LuChevronDown,
  LuSigma,
  LuPercent,
  LuLayers,
  LuTarget,
  LuChartLine,
  LuMountain,
} from 'react-icons/lu'

import { analyticsService } from '../../../api/services/analytics.service'
import type {
  LaborStabilityResponse,
  LaborStabilityFiltersResponse,
  LaborStabilityStatus,
  LaborStabilityQuery,
  LaborStabilityMiningRightOption,
} from '../../../api/models/response/laborStability'
import { useHasPermission } from '../../../auth/PermissionContext'
import {
  ReportPage,
  ReportHeader,
  ActionButton,
  FilterBar,
  FilterField,
  VerdictBanner,
  KpiGrid,
  KpiTile,
  Panel,
  ToneBadge,
  Notice,
  EmptyState,
  ReportSkeleton,
} from '../../../components/analytics/ReportKit'
import { reportAccent, TONES, chart, selectStyle, table, type Tone } from '../../../components/analytics/reportTheme'
import { fonts, mineral, shadows } from '../../../theme'

/* ─────────────────────────── Estados del semáforo ─────────────────────────── */

const STATUS_INFO: Record<LaborStabilityStatus, { label: string; tone: Tone; plain: string }> = {
  Insufficient: {
    label: 'Datos insuficientes',
    tone: 'neutral',
    plain: 'Hacen falta al menos 2 lotes con ley de laboratorio para medir la variabilidad (y 3 para detectar saltos).',
  },
  Stable: {
    label: 'Estable y natural',
    tone: 'ok',
    plain: 'La ley varía lote a lote como se espera de un yacimiento real. No hay señales de sustitución.',
  },
  HomogeneityWarning: {
    label: 'Homogeneidad sospechosa',
    tone: 'warn',
    plain: 'La ley es más pareja de lo que la geología suele permitir. Conviene revisar de dónde viene el mineral.',
  },
  HomogeneityCritical: {
    label: 'Homogeneidad artificial',
    tone: 'bad',
    plain: 'Leyes casi idénticas lote a lote: patrón típico de mineral mezclado u homogeneizado en un acopio.',
  },
  GeologicalJump: {
    label: 'Salto geológico imposible',
    tone: 'bad',
    plain: 'Al menos un lote se aleja tanto del histórico de la labor que difícilmente proviene de la misma veta.',
  },
}

/* ─────────────────────────── Formato es-PE ─────────────────────────── */

function fmt(value: number | null | undefined, digits = 2): string {
  if (value == null || !Number.isFinite(value)) return '—'
  return value.toLocaleString('es-PE', { maximumFractionDigits: digits })
}

/** Decimales según magnitud: 16,3 g/t · 0,2541 oz/t · 125,4 g/t. */
function gradeDigits(v: number): number {
  const a = Math.abs(v)
  if (a >= 100) return 1
  if (a >= 1) return 2
  return 4
}
const fmtGrade = (v: number) => fmt(v, gradeDigits(v))
const fmtPct = (v: number) => fmt(v, 2)
const fmtInt = (v: number) => Math.round(v).toLocaleString('es-PE')

function fmtDate(iso: string): string {
  const d = new Date(iso + 'T00:00:00')
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString('es-PE', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

function fmtDayShort(iso: string): string {
  const d = new Date(iso + 'T00:00:00')
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString('es-PE', { day: '2-digit', month: 'short' })
}

/* ─────────────────────────── Selección por defecto ─────────────────────────── */

interface Combo { minerId: string; miningRightId: string; mineralTypeId: string; lots: number }

function lotsOf(right: LaborStabilityMiningRightOption | null | undefined, mineralTypeId: string): number {
  return right?.mineralLots?.find((m) => m.mineralTypeId === mineralTypeId)?.lots ?? 0
}

/** Mineral con más lotes en el derecho; si no hay datos, Au del catálogo. */
function bestMineralFor(right: LaborStabilityMiningRightOption | null | undefined, f: LaborStabilityFiltersResponse): string {
  const listed = new Set(f.minerals.map((m) => m.mineralTypeId))
  const top = right?.mineralLots?.find((m) => listed.has(m.mineralTypeId))
  if (top && top.lots > 0) return top.mineralTypeId
  return (f.minerals.find((m) => m.code === 'AU') ?? f.minerals[0])?.mineralTypeId ?? ''
}

/**
 * La combinación titular → derecho → mineral con MÁS lotes con ley. Antes se tomaba
 * el primer titular alfabético y su primer derecho, que podía tener 1 solo lote y
 * abrir el reporte en "Datos insuficientes".
 */
function bestCombo(f: LaborStabilityFiltersResponse): Combo | null {
  // Sólo minerales que el selector ofrece (pagables), o el select quedaría desfasado.
  const listed = new Set(f.minerals.map((m) => m.mineralTypeId))
  let best: Combo | null = null
  for (const m of f.miners) {
    for (const r of m.miningRights) {
      for (const ml of r.mineralLots ?? []) {
        if (!listed.has(ml.mineralTypeId)) continue
        if (!best || ml.lots > best.lots) {
          best = { minerId: m.minerId, miningRightId: r.miningRightId, mineralTypeId: ml.mineralTypeId, lots: ml.lots }
        }
      }
    }
  }
  return best && best.lots > 0 ? best : null
}

/** ¿El backend envía recuentos de lotes por mineral? */
function hasLotCounts(f: LaborStabilityFiltersResponse): boolean {
  return f.miners.some((m) => m.miningRights.some((r) => r.mineralLots !== undefined))
}

function rightTotalLots(r: LaborStabilityMiningRightOption): number {
  return Math.max(0, ...(r.mineralLots ?? []).map((m) => m.lots))
}

/* ─────────────────────────── Página ─────────────────────────── */

type Busy = 'pdf' | 'seal' | 'expediente' | null

export default function EstabilidadLaborPage() {
  const [filters, setFilters] = useState<LaborStabilityFiltersResponse | null>(null)
  const [filtersLoading, setFiltersLoading] = useState(true)
  const [minerId, setMinerId] = useState('')
  const [miningRightId, setMiningRightId] = useState('')
  const [componentId, setComponentId] = useState('')
  const [mineralTypeId, setMineralTypeId] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')

  const [data, setData] = useState<LaborStabilityResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [msg, setMsg] = useState<string | null>(null)
  const [busy, setBusy] = useState<Busy>(null)

  const canConfigure = useHasPermission('analytics.laborStability.configure')
  const requestSeq = useRef(0)

  // Selectores en cascada derivados de los filtros cargados.
  const miner = useMemo(() => filters?.miners.find((m) => m.minerId === minerId) ?? null, [filters, minerId])
  const rights = useMemo(() => miner?.miningRights ?? [], [miner])
  const right = useMemo(() => rights.find((r) => r.miningRightId === miningRightId) ?? null, [rights, miningRightId])
  const components = right?.components ?? []

  // Minerales: primero los que tienen lotes en el derecho elegido (con su recuento).
  const minerals = useMemo(() => {
    const list = filters?.minerals ?? []
    return [...list]
      .map((m) => ({ ...m, lots: lotsOf(right, m.mineralTypeId) }))
      .sort((a, b) => b.lots - a.lots || a.code.localeCompare(b.code))
  }, [filters, right])

  const best = useMemo(() => (filters ? bestCombo(filters) : null), [filters])
  const currentLots = lotsOf(right, mineralTypeId)

  const query: LaborStabilityQuery | null = useMemo(() => {
    if (!miningRightId || !mineralTypeId) return null
    return {
      miningRightId,
      mineralTypeId,
      componentId: componentId || undefined,
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
    }
  }, [miningRightId, mineralTypeId, componentId, dateFrom, dateTo])

  const applyCombo = useCallback((c: Combo) => {
    setMinerId(c.minerId)
    setMiningRightId(c.miningRightId)
    setMineralTypeId(c.mineralTypeId)
    setComponentId('')
  }, [])

  // Carga inicial de filtros + preselección de la labor con más lotes con ley.
  useEffect(() => {
    void (async () => {
      try {
        const f = await analyticsService.laborStabilityFilters()
        setFilters(f)
        const c = bestCombo(f)
        if (c) applyCombo(c)
        else if (!hasLotCounts(f)) {
          // API sin recuentos (versión anterior del backend): primera labor disponible.
          const m0 = f.miners[0]
          const r0 = m0?.miningRights[0]
          if (m0 && r0) applyCombo({ minerId: m0.minerId, miningRightId: r0.miningRightId, mineralTypeId: bestMineralFor(r0, f), lots: 0 })
        }
        // Sin lotes con ley en ninguna labor: no se preselecciona nada y la página
        // muestra un estado vacío que explica qué falta.
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e))
      } finally {
        setFiltersLoading(false)
      }
    })()
  }, [applyCombo])

  const fetchData = useCallback(async () => {
    if (!query) {
      setData(null)
      return
    }
    // Descarta respuestas viejas si el usuario cambia filtros rápido.
    const seq = ++requestSeq.current
    setLoading(true)
    setError(null)
    try {
      const res = await analyticsService.laborStability(query)
      if (seq === requestSeq.current) setData(res)
    } catch (e) {
      if (seq === requestSeq.current) setError(e instanceof Error ? e.message : String(e))
    } finally {
      if (seq === requestSeq.current) setLoading(false)
    }
  }, [query])

  useEffect(() => {
    void fetchData()
  }, [fetchData])

  const onMinerChange = (id: string) => {
    setMinerId(id)
    const m = filters?.miners.find((x) => x.minerId === id)
    // Derecho con más lotes del titular (no el primero alfabético).
    const r = [...(m?.miningRights ?? [])].sort((a, b) => rightTotalLots(b) - rightTotalLots(a))[0]
    setMiningRightId(r?.miningRightId ?? '')
    if (r && filters && lotsOf(r, mineralTypeId) === 0) setMineralTypeId(bestMineralFor(r, filters))
    setComponentId('')
  }

  const onRightChange = (id: string) => {
    setMiningRightId(id)
    const r = rights.find((x) => x.miningRightId === id)
    if (r && filters && lotsOf(r, mineralTypeId) === 0) setMineralTypeId(bestMineralFor(r, filters))
    setComponentId('')
  }

  const flash = (text: string) => {
    setError(null)
    setMsg(text)
  }

  const fileBase = data ? `estabilidad-labor-${data.context.miningRightCode ?? 'labor'}-${data.asOf}` : 'estabilidad-labor'

  const exportCsv = () => {
    if (!data) return
    const unit = data.context.gradeUnit ?? ''
    const header = `Fecha,Lote,Ley (${unit}),ZScore,FueraDeControl`
    const rows = data.series.map((p) =>
      [p.date, csv(p.batchCode), p.grade, p.zScore ?? '', p.outOfControl ? 'Sí' : 'No'].join(','),
    )
    const blob = new Blob(['﻿' + [header, ...rows].join('\n')], { type: 'text/csv;charset=utf-8;' })
    download(blob, `${fileBase}.csv`)
  }

  const exportPdf = async () => {
    if (!query || !data) return
    setMsg(null)
    setBusy('pdf')
    try {
      const url = analyticsService.laborStabilityPdfUrl(query)
      const token = localStorage.getItem('auth_token')
      const tenantId = localStorage.getItem('tenant_id')
      const res = await fetch(url, {
        headers: { Authorization: token ? `Bearer ${token}` : '', 'X-Tenant-Id': tenantId ?? '' },
      })
      if (!res.ok) throw new Error('No se pudo generar el PDF')
      download(await res.blob(), `${fileBase}.pdf`)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(null)
    }
  }

  const saveSnapshot = async () => {
    if (!query) return
    setMsg(null)
    setBusy('seal')
    try {
      const snap = await analyticsService.saveLaborStabilitySnapshot(query)
      flash(`Reporte sellado y guardado cifrado (SHA-256 ${snap.contentSha256.slice(0, 12)}…).`)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(null)
    }
  }

  const appendToExpediente = async () => {
    if (!query) return
    setMsg(null)
    setBusy('expediente')
    try {
      const r = await analyticsService.appendLaborStabilityToExpediente(query)
      flash(
        r.alreadyExisted
          ? `Ya estaba consolidado en el expediente ${r.expedienteCode} (ítem ${r.order}).`
          : `Consolidado en el expediente ${r.expedienteCode} (ítem ${r.order}).`,
      )
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(null)
    }
  }

  const unit = data?.context.gradeUnit ?? undefined
  const withCounts = !!filters && hasLotCounts(filters)
  const hasLots = !!best || (!!filters && !hasLotCounts(filters) && filters.miners.length > 0)
  const showBetter = withCounts && !!best && currentLots < 2 && best.lots >= 2 &&
    !(best.miningRightId === miningRightId && best.mineralTypeId === mineralTypeId)
  const bestRightName = useMemo(() => {
    if (!best || !filters) return ''
    const r = filters.miners.flatMap((m) => m.miningRights).find((x) => x.miningRightId === best.miningRightId)
    const mn = filters.minerals.find((x) => x.mineralTypeId === best.mineralTypeId)
    return `${r?.name ?? 'otra labor'}${mn ? ` · ${mn.name}` : ''}`
  }, [best, filters])

  return (
    <ReportPage>
      <ReportHeader
        icon={LuActivity}
        title="Estabilidad de la Labor"
        subtitle="Histórico de la ley de laboratorio por labor minera: detecta sustitución o blanqueo de mineral (homogeneidad artificial o saltos geológicos imposibles)."
        actions={
          <>
            <ActionButton
              icon={LuRefreshCw}
              label="Refrescar"
              variant="ghost"
              loading={loading}
              disabled={!query}
              onClick={() => void fetchData()}
            />
            <ExportMenu
              disabled={!data}
              items={[
                { key: 'csv', icon: LuFileSpreadsheet, label: 'Serie en CSV', hint: 'Fechas, leyes y Z de cada lote', onClick: exportCsv },
                { key: 'pdf', icon: LuFileText, label: 'Reporte en PDF', hint: 'Informe forense con métricas y anomalías', onClick: () => void exportPdf(), busy: busy === 'pdf' },
                ...(canConfigure
                  ? [{
                      key: 'seal', icon: LuShieldCheck, label: 'Sellar evidencia',
                      hint: 'Guarda una copia cifrada con huella SHA-256', onClick: () => void saveSnapshot(),
                      busy: busy === 'seal', section: 'Cumplimiento',
                    }]
                  : []),
              ]}
            />
            {canConfigure && (
              <ActionButton
                icon={LuFolderPlus}
                label="Consolidar en expediente"
                variant="primary"
                loading={busy === 'expediente'}
                disabled={!data || data.metrics.n === 0}
                title="Anexa el reporte sellado al expediente del minero"
                onClick={() => void appendToExpediente()}
              />
            )}
          </>
        }
      />

      {/* Filtros */}
      <FilterBar
        note={
          right
            ? <>Se analizan los últimos lotes con ley de laboratorio del derecho minero. La bocamina es referencia del reporte: los lotes aún no registran de qué bocamina salen.</>
            : undefined
        }
      >
        <FilterField label="Titular / minero" width={{ base: '100%', md: '260px' }}>
          <select style={selectStyle} value={minerId} disabled={!filters || filters.miners.length === 0}
            onChange={(e) => onMinerChange(e.target.value)}>
            {!minerId && <option value="">Selecciona un titular</option>}
            {filters?.miners.map((m) => (
              <option key={m.minerId} value={m.minerId}>
                {m.name}{m.ruc ? ` — ${m.ruc}` : ''}
              </option>
            ))}
          </select>
        </FilterField>

        <FilterField label="Concesión / derecho" width={{ base: '100%', md: '230px' }}>
          <select style={selectStyle} value={miningRightId} disabled={rights.length === 0}
            onChange={(e) => onRightChange(e.target.value)}>
            {!miningRightId && <option value="">—</option>}
            {rights.map((r) => {
              const n = rightTotalLots(r)
              return (
                <option key={r.miningRightId} value={r.miningRightId}>
                  {r.name}{r.code ? ` (${r.code})` : ''}{withCounts ? ` · ${n === 0 ? 'sin ley' : `${n} ${n === 1 ? 'lote' : 'lotes'}`}` : ''}
                </option>
              )
            })}
          </select>
        </FilterField>

        <FilterField label="Bocamina" width={{ base: '100%', md: '180px' }}>
          <select style={selectStyle} value={componentId} disabled={components.length === 0}
            onChange={(e) => setComponentId(e.target.value)}>
            <option value="">Todo el derecho</option>
            {components.map((c) => (
              <option key={c.componentId} value={c.componentId}>{c.name}</option>
            ))}
          </select>
        </FilterField>

        <FilterField label="Mineral" width={{ base: '100%', md: '190px' }}>
          <select style={selectStyle} value={mineralTypeId} disabled={minerals.length === 0}
            onChange={(e) => setMineralTypeId(e.target.value)}>
            {minerals.map((m) => (
              <option key={m.mineralTypeId} value={m.mineralTypeId}>
                {m.name} ({m.code}){withCounts ? ` · ${m.lots === 0 ? 'sin lotes' : `${m.lots} ${m.lots === 1 ? 'lote' : 'lotes'}`}` : ''}
              </option>
            ))}
          </select>
        </FilterField>

        <FilterField label="Periodo de admisión" width={{ base: '100%', md: '300px' }}>
          <Flex gap={2} align="center">
            <Box flex={1} minW={0}>
              <DatePicker value={dateFrom} onChange={setDateFrom} maxDate={dateTo || undefined} placeholder="Desde" />
            </Box>
            <Text fontSize="12px" color={mineral.ink4} flexShrink={0}>a</Text>
            <Box flex={1} minW={0}>
              <DatePicker value={dateTo} onChange={setDateTo} minDate={dateFrom || undefined} placeholder="Hasta" />
            </Box>
          </Flex>
        </FilterField>
      </FilterBar>

      {/* Mensajes de acciones */}
      <AnimatePresence>
        {error && (
          <motion.div key="err" initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}>
            <Notice tone="bad">{error}</Notice>
          </motion.div>
        )}
        {msg && (
          <motion.div key="msg" initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}>
            <Notice tone="ok">{msg}</Notice>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Primera carga */}
      {(filtersLoading || (loading && !data)) && <ReportSkeleton />}

      {/* Sin ninguna labor con ley de laboratorio */}
      {!filtersLoading && filters && !hasLots && !data && (
        <Panel>
          <EmptyState
            icon={LuMountain}
            title="Aún no hay lotes con ley de laboratorio"
            hint="El reporte se arma con la ley pagable de la muestra de laboratorio de planta. Cuando los lotes de una labor tengan análisis cargado, aparecerán aquí."
          />
        </Panel>
      )}

      {!filtersLoading && hasLots && !query && !loading && (
        <Panel>
          <EmptyState icon={LuTarget} title="Elige una labor para analizar" hint="Selecciona un titular, una concesión y un mineral." />
        </Panel>
      )}

      {data && (
        <motion.div animate={{ opacity: loading ? 0.55 : 1 }} transition={{ duration: 0.2 }}>
          <Flex direction="column" gap={5}>
            <Verdict data={data} />

            {showBetter && best && (
              <Notice tone="info">
                <Flex align="center" gap={3} wrap="wrap">
                  <Text>
                    {currentLots === 0 ? 'Esta combinación no tiene lotes con ley.' : 'Esta combinación tiene 1 solo lote con ley.'}{' '}
                    {bestRightName} tiene {fmtInt(best.lots)} lotes.
                  </Text>
                  <Box as="button" {...{ type: 'button' }} color={reportAccent.deep} fontWeight={600} textDecoration="underline"
                    cursor="pointer" onClick={() => applyCombo(best)}>
                    Ver esa labor
                  </Box>
                </Flex>
              </Notice>
            )}

            <Kpis data={data} unit={unit} />

            <Diagnostics data={data} />

            <Panel
              title="Tendencia de la ley"
              subtitle={`Ley de cada lote por fecha de admisión, con la media y las franjas de control de la propia labor.`}
              index={1}
            >
              <StabilityChart data={data} />
            </Panel>

            <Grid templateColumns={{ base: '1fr', xl: '1fr 340px' }} gap={5} alignItems="start">
              <SeriesTable data={data} />
              <ThresholdsPanel data={data} canConfigure={canConfigure} />
            </Grid>
          </Flex>
        </motion.div>
      )}
    </ReportPage>
  )
}

/* ─────────────────────────── Veredicto ─────────────────────────── */

function Verdict({ data }: { data: LaborStabilityResponse }) {
  const info = STATUS_INFO[data.status]
  const ctx = data.context
  const where = [ctx.miningRightName, ctx.componentName, ctx.mineralName ?? ctx.mineralCode].filter(Boolean).join(' · ')
  const first = data.series[0]
  const last = data.series[data.series.length - 1]
  // Insuficiente: el mensaje del motor dice exactamente qué falta (0 lotes vs 1).
  const n = data.metrics.n
  const subtitle = data.status !== 'Insufficient'
    ? info.plain
    : n === 0
      ? 'No hay lotes con ley de laboratorio para esta labor, mineral y periodo.'
      : n === 1
        ? 'Solo hay 1 lote con ley: se necesitan al menos 2 para medir la variabilidad y 3 para detectar saltos.'
        : (data.alerts[0] ?? info.plain)
  return (
    <VerdictBanner
      tone={info.tone}
      title={info.label}
      subtitle={<>{subtitle}{where && <Text as="span" display="block" color={mineral.ink3} mt={0.5}>{where}</Text>}</>}
      meta={
        <>
          <Text fontFamily={fonts.display} fontWeight={700} fontSize="15px" color={mineral.ink}>
            {fmtInt(data.metrics.n)} {data.metrics.n === 1 ? 'lote analizado' : 'lotes analizados'}
          </Text>
          {first && last && (
            <Text fontSize="12px" color={mineral.ink3}>
              {first.date === last.date ? fmtDate(first.date) : `${fmtDate(first.date)} – ${fmtDate(last.date)}`}
            </Text>
          )}
        </>
      }
    />
  )
}

/* ─────────────────────────── KPIs ─────────────────────────── */

function cvTone(status: LaborStabilityStatus): Tone | undefined {
  if (status === 'HomogeneityCritical') return 'bad'
  if (status === 'HomogeneityWarning') return 'warn'
  if (status === 'Stable' || status === 'GeologicalJump') return 'ok'
  return undefined
}

function Kpis({ data, unit }: { data: LaborStabilityResponse; unit?: string }) {
  const m = data.metrics
  const t = data.thresholds
  // σ muestral (n − 1) sólo existe con 2 lotes o más: con 1 lote el backend trae 0
  // por defecto, y mostrar "σ = 0" sugeriría una homogeneidad perfecta que no existe.
  const hasSpread = m.n >= 2
  return (
    <KpiGrid>
      <KpiTile
        index={0}
        icon={LuChartLine}
        label="Ley media (x̄)"
        value={m.n > 0 ? m.mean : null}
        format={fmtGrade}
        unit={unit}
        hint={m.n > 0 ? `Promedio de los lotes · rango ${fmtGrade(m.min)} – ${fmtGrade(m.max)}` : 'Sin lotes con ley'}
      />
      <KpiTile
        index={1}
        icon={LuSigma}
        label="Desviación estándar (σ)"
        value={hasSpread ? m.stdDev : null}
        format={fmtGrade}
        unit={unit}
        hint={hasSpread ? 'Cuánto se aleja, en promedio, cada lote de la media' : 'Se necesita más de 1 lote'}
      />
      <KpiTile
        index={2}
        icon={LuPercent}
        label="Coeficiente de variación (CV)"
        value={hasSpread && m.cv != null ? m.cv : null}
        format={fmtPct}
        unit="%"
        tone={hasSpread && m.cv != null ? cvTone(data.status) : undefined}
        hint={`σ como % de la media. Bajo ${fmt(t.cvMinPercent)} % la ley es sospechosamente pareja`}
      />
      <KpiTile
        index={3}
        icon={LuLayers}
        label="Lotes analizados"
        value={m.n}
        format={fmtInt}
        hint={m.lastZScore != null
          ? `Último lote a ${fmt(m.lastZScore)} σ del histórico previo`
          : `Ventana: últimos ${fmtInt(t.windowSize)} lotes`}
      />
    </KpiGrid>
  )
}

/* ─────────────────────────── Diagnóstico forense ─────────────────────────── */

function Diagnostics({ data }: { data: LaborStabilityResponse }) {
  if (data.status === 'Insufficient') return null
  if (data.anomalies.length === 0) {
    return <Notice tone="ok">{data.alerts[0] ?? STATUS_INFO.Stable.plain}</Notice>
  }
  return (
    <Panel title="Diagnóstico forense" subtitle="Qué disparó el semáforo y en qué lotes." index={0}>
      <Flex direction="column" gap={2.5}>
        {data.anomalies.map((a, i) => {
          const tone: Tone = a.severity === 'Critical' ? 'bad' : 'warn'
          return (
            <motion.div key={i} initial={{ opacity: 0, x: -6 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.04 * i }}>
              <Flex gap={3} align="flex-start" px={3.5} py={3} borderRadius="12px" bg={mineral.surfaceAlt} border={`1px solid ${mineral.line}`}>
                <Box flexShrink={0} pt="1px">
                  <ToneBadge tone={tone}>{a.type === 'GeologicalJump' ? 'Salto' : 'Homogeneidad'}</ToneBadge>
                </Box>
                <Box minW={0}>
                  <Text fontSize="13px" color={mineral.ink2}>{a.message}</Text>
                  {(a.batchCode || a.date) && (
                    <Text fontSize="11.5px" color={mineral.ink3} mt={0.5}>
                      {[a.batchCode, a.date ? fmtDate(a.date) : null, a.zScore != null ? `Z ${fmt(a.zScore)}` : null].filter(Boolean).join(' · ')}
                    </Text>
                  )}
                </Box>
              </Flex>
            </motion.div>
          )
        })}
      </Flex>
    </Panel>
  )
}

/* ─────────────────────────── Gráfico de control ─────────────────────────── */

interface ChartPoint {
  idx: number
  label: string
  date: string
  grade: number
  outlierGrade: number | null
  z: number | null
  batchCode: string
  outOfControl: boolean
}

function StabilityChart({ data }: { data: LaborStabilityResponse }) {
  const unit = data.context.gradeUnit ?? ''
  const b = data.bands
  const withBands = data.metrics.n >= 2 && data.status !== 'Insufficient'

  if (data.series.length < 2) {
    return (
      <EmptyState
        icon={LuChartLine}
        title={data.series.length === 0 ? 'Sin lotes en el periodo' : 'Un solo lote: aún no hay tendencia'}
        hint={data.series.length === 0
          ? 'Amplía el periodo o elige otra labor o mineral.'
          : `El lote ${data.series[0]?.batchCode ?? ''} tiene ${fmtGrade(data.series[0]?.grade ?? 0)} ${unit}. Con el segundo lote se dibujan la media y las franjas.`}
      />
    )
  }

  const chartData: ChartPoint[] = data.series.map((p, idx) => ({
    idx,
    label: fmtDayShort(p.date),
    date: p.date,
    grade: p.grade,
    outlierGrade: p.outOfControl ? p.grade : null,
    z: p.zScore ?? null,
    batchCode: p.batchCode,
    outOfControl: p.outOfControl,
  }))

  const ys = [
    ...data.series.map((p) => p.grade),
    ...(withBands ? [b.lowerControl, b.upperControl, b.mean] : []),
  ]
  const yMin = Math.min(...ys)
  const yMax = Math.max(...ys)
  const span = yMax - yMin
  const pad = span > 0 ? span * 0.08 : Math.max(Math.abs(yMax) * 0.1, 0.1)
  // Sin redondear a enteros: con leyes en oz/t (0,25) el piso/techo entero aplastaba la serie.
  const domain: [number, number] = [Math.max(0, yMin - pad), yMax + pad]

  const ok = TONES.ok
  const bad = TONES.bad
  const kLabel = fmt(b.sigmaK, 1)
  const zLabel = fmt(data.thresholds.zThreshold, 1)

  return (
    <Box>
      <Box h={{ base: '280px', md: '340px' }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={chartData} margin={{ left: 4, right: 12, top: 8, bottom: 0 }}>
            <CartesianGrid {...chart.grid} />
            <XAxis
              dataKey="idx"
              type="number"
              domain={[-0.5, chartData.length - 0.5]}
              ticks={chartData.map((p) => p.idx)}
              interval="preserveStartEnd"
              tickFormatter={(i: number) => chartData[i]?.label ?? ''}
              {...chart.axis}
            />
            <YAxis domain={domain} width={56} tickFormatter={(v: number) => fmtGrade(v)} {...chart.axis} />
            {withBands && (
              <>
                {/* Franja de control (±Zσ) y de aviso (±kσ): suaves, sin borde */}
                <ReferenceArea y1={b.lowerControl} y2={b.upperControl} fill={ok.color} fillOpacity={0.06} ifOverflow="hidden" />
                <ReferenceArea y1={b.lowerWarn} y2={b.upperWarn} fill={ok.color} fillOpacity={0.1} ifOverflow="hidden" />
                <ReferenceLine y={b.mean} stroke={reportAccent.deep} strokeDasharray="5 5" strokeOpacity={0.7}
                  label={{ value: 'x̄', position: 'right', fill: reportAccent.deep, fontSize: 11 }} />
              </>
            )}
            <Tooltip content={<StabilityTooltip unit={unit} />} cursor={{ stroke: mineral.lineStrong, strokeDasharray: '3 3' }} />
            <Line
              type="monotone"
              dataKey="grade"
              stroke={reportAccent.color}
              strokeWidth={2.25}
              dot={{ r: 3, fill: '#fff', stroke: reportAccent.color, strokeWidth: 2 }}
              activeDot={{ r: 5, fill: reportAccent.color }}
              isAnimationActive={false}
            />
            <Scatter dataKey="outlierGrade" fill={bad.color} shape="circle" isAnimationActive={false} />
          </ComposedChart>
        </ResponsiveContainer>
      </Box>

      {/* Leyenda */}
      <Flex gap={4} wrap="wrap" mt={3} fontSize="12px" color={mineral.ink2}>
        <LegendItem swatch={<Box w="16px" h="2.5px" borderRadius="full" bg={reportAccent.color} />} label={`Ley del lote${unit ? ` (${unit})` : ''}`} />
        {withBands && (
          <>
            <LegendItem swatch={<Box w="16px" borderTop={`2px dashed ${reportAccent.deep}`} />} label="Media x̄" />
            <LegendItem swatch={<Box w="14px" h="10px" borderRadius="3px" bg={ok.color} opacity={0.3} />} label={`Aviso ±${kLabel}σ`} />
            <LegendItem swatch={<Box w="14px" h="10px" borderRadius="3px" bg={ok.color} opacity={0.14} />} label={`Control ±${zLabel}σ`} />
          </>
        )}
        <LegendItem swatch={<Box w="9px" h="9px" borderRadius="full" bg={bad.color} />} label="Fuera de control" />
      </Flex>
    </Box>
  )
}

function LegendItem({ swatch, label }: { swatch: React.ReactNode; label: string }) {
  return (
    <Flex align="center" gap={1.5}>
      {swatch}
      <Text>{label}</Text>
    </Flex>
  )
}

interface TooltipPayloadItem { payload: ChartPoint }
function StabilityTooltip({ active, payload, unit }: { active?: boolean; payload?: TooltipPayloadItem[]; unit: string }) {
  const p = payload?.[0]?.payload
  if (!active || !p) return null
  return (
    <Box style={chart.tooltip.contentStyle}>
      <Text fontSize="12px" fontWeight={700} color={mineral.ink}>{p.batchCode}</Text>
      <Text fontSize="11.5px" color={mineral.ink3} mb={1}>{fmtDate(p.date)}</Text>
      <Text fontSize="12px" color={mineral.ink2}>Ley: <b>{fmtGrade(p.grade)}</b> {unit}</Text>
      <Text fontSize="12px" color={mineral.ink2}>Z: {p.z != null ? fmt(p.z) : '—'}</Text>
      {p.outOfControl && <Box mt={1.5}><ToneBadge tone="bad">Fuera de control</ToneBadge></Box>}
    </Box>
  )
}

/* ─────────────────────────── Umbrales ─────────────────────────── */

function ThresholdsPanel({ data, canConfigure }: { data: LaborStabilityResponse; canConfigure: boolean }) {
  const navigate = useNavigate()
  const t = data.thresholds
  const items: { label: string; value: string; hint: string }[] = [
    { label: 'CV mínimo natural (θ_min)', value: `${fmt(t.cvMinPercent)} %`, hint: 'Por debajo: homogeneidad sospechosa' },
    { label: 'CV crítico (θ_crit)', value: `${fmt(t.cvCriticalPercent)} %`, hint: 'Por debajo: homogeneidad artificial' },
    { label: 'Umbral de salto (Z)', value: `${fmt(t.zThreshold, 1)} σ`, hint: 'Un lote más allá es salto imposible' },
    { label: 'Franja de aviso (k)', value: `±${fmt(t.bandSigmaK, 1)} σ`, hint: 'Banda interna del gráfico' },
    { label: 'Ventana', value: `${fmtInt(t.windowSize)} lotes`, hint: 'Últimos lotes considerados' },
  ]
  return (
    <Panel
      title="Umbrales vigentes"
      subtitle="Criterios geológicos de la planta."
      index={3}
      actions={canConfigure
        ? <ActionButton icon={LuSettings} label="Configurar" variant="ghost" onClick={() => navigate('/app/analitica/configuracion')} />
        : undefined}
    >
      <Flex direction="column" gap={0}>
        {items.map((it, i) => (
          <Flex key={it.label} justify="space-between" align="center" gap={3} py={2.5}
            borderTop={i === 0 ? 'none' : `1px solid ${mineral.line}`}>
            <Box minW={0}>
              <Text fontSize="13px" color={mineral.ink2} fontWeight={500}>{it.label}</Text>
              <Text fontSize="11.5px" color={mineral.ink3}>{it.hint}</Text>
            </Box>
            <Text fontFamily={fonts.mono} fontSize="13px" fontWeight={600} color={mineral.ink} flexShrink={0}>{it.value}</Text>
          </Flex>
        ))}
      </Flex>
    </Panel>
  )
}

/* ─────────────────────────── Tabla de la serie ─────────────────────────── */

function SeriesTable({ data }: { data: LaborStabilityResponse }) {
  const unit = data.context.gradeUnit ?? ''
  // Lo más reciente primero: es lo que el oficial revisa.
  const rows = [...data.series].reverse()
  return (
    <Panel title="Lotes analizados" subtitle={`${fmtInt(rows.length)} ${rows.length === 1 ? 'lote' : 'lotes'}, del más reciente al más antiguo.`} padded={false} index={2}>
      {rows.length === 0 ? (
        <EmptyState title="Sin lotes con ley de laboratorio" hint="No hay ensayos de planta para esta labor, mineral y periodo." />
      ) : (
        <Box overflowX="auto" maxH="460px" overflowY="auto">
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
              <tr>
                <th style={table.th}>Fecha</th>
                <th style={table.th}>Lote</th>
                <th style={{ ...table.th, textAlign: 'right' }}>Ley{unit ? ` (${unit})` : ''}</th>
                <th style={{ ...table.th, textAlign: 'right' }}>Z</th>
                <th style={{ ...table.th, textAlign: 'right' }}>Control</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <tr key={p.shipmentRecordId} style={p.outOfControl ? { background: TONES.bad.soft } : undefined}>
                  <td style={{ ...table.td, whiteSpace: 'nowrap' }}>{fmtDate(p.date)}</td>
                  <td style={{ ...table.td, fontWeight: 600, color: mineral.ink }}>{p.batchCode}</td>
                  <td style={{ ...table.td, textAlign: 'right', fontFamily: fonts.mono, color: mineral.ink }}>{fmtGrade(p.grade)}</td>
                  <td style={{ ...table.td, textAlign: 'right', fontFamily: fonts.mono }}>{p.zScore != null ? fmt(p.zScore) : '—'}</td>
                  <td style={{ ...table.td, textAlign: 'right' }}>
                    {p.outOfControl ? <ToneBadge tone="bad">Fuera</ToneBadge> : <ToneBadge tone="ok">En control</ToneBadge>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Box>
      )}
    </Panel>
  )
}

/* ─────────────────────────── Menú de exportación ─────────────────────────── */

interface ExportItem {
  key: string
  icon: IconType
  label: string
  hint: string
  onClick: () => void
  busy?: boolean
  section?: string
}

/** Agrupa las descargas (CSV, PDF) y el sellado en un único botón con menú. */
function ExportMenu({ items, disabled }: { items: ExportItem[]; disabled?: boolean }) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const anyBusy = items.some((i) => i.busy)

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  return (
    <Box position="relative" ref={ref}>
      <Flex as="button" {...{ type: 'button', disabled, 'aria-haspopup': 'menu', 'aria-expanded': open }}
        align="center" gap={2} h="38px" px={3.5} borderRadius="10px" fontSize="13px" fontWeight={600} whiteSpace="nowrap"
        bg="#fff" color={mineral.ink2} border={`1px solid ${open ? reportAccent.color : mineral.line}`}
        cursor={disabled ? 'not-allowed' : 'pointer'} opacity={disabled ? 0.5 : 1} transition="all .15s"
        _hover={disabled ? {} : { borderColor: reportAccent.color, color: reportAccent.deep }}
        onClick={disabled ? undefined : () => setOpen((o) => !o)}>
        <motion.span animate={anyBusy ? { rotate: 360 } : { rotate: 0 }}
          transition={anyBusy ? { duration: 0.9, repeat: Infinity, ease: 'linear' } : { duration: 0.2 }} style={{ display: 'flex' }}>
          {anyBusy ? <LuRefreshCw size={15} /> : <LuDownload size={15} />}
        </motion.span>
        Exportar
        <motion.span animate={{ rotate: open ? 180 : 0 }} transition={{ duration: 0.2 }} style={{ display: 'flex' }}>
          <LuChevronDown size={14} />
        </motion.span>
      </Flex>

      <AnimatePresence>
        {open && (
          <motion.div
            role="menu"
            initial={{ opacity: 0, y: -6, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.98 }}
            transition={{ duration: 0.16 }}
            style={{
              position: 'absolute', right: 0, top: 'calc(100% + 6px)', zIndex: 20, width: 280,
              background: '#fff', border: `1px solid ${mineral.line}`, borderRadius: 14, boxShadow: shadows.lg,
              padding: 6,
            }}
          >
            {items.map((it, i) => (
              <Box key={it.key}>
                {it.section && (
                  <Text fontSize="10.5px" fontWeight={600} color={mineral.ink3} textTransform="uppercase" letterSpacing="0.06em"
                    px={2.5} pt={i === 0 ? 1 : 2.5} pb={1} borderTop={i === 0 ? 'none' : `1px solid ${mineral.line}`} mt={i === 0 ? 0 : 1}>
                    {it.section}
                  </Text>
                )}
                <Flex as="button" {...{ type: 'button', role: 'menuitem', disabled: it.busy }} w="100%" textAlign="left" align="flex-start"
                  gap={2.5} px={2.5} py={2} borderRadius="10px" cursor={it.busy ? 'wait' : 'pointer'}
                  _hover={{ bg: reportAccent.soft }} transition="background .12s"
                  onClick={() => { setOpen(false); it.onClick() }}>
                  <Box color={reportAccent.color} display="flex" mt="2px"><it.icon size={16} /></Box>
                  <Box minW={0}>
                    <Text fontSize="13px" fontWeight={600} color={mineral.ink}>{it.label}</Text>
                    <Text fontSize="11.5px" color={mineral.ink3}>{it.hint}</Text>
                  </Box>
                </Flex>
              </Box>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </Box>
  )
}

/* ─────────────────────────── Auxiliares ─────────────────────────── */

function download(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = fileName
  a.click()
  URL.revokeObjectURL(url)
}

function csv(value: string): string {
  if (value.includes(',') || value.includes('"') || value.includes('\n')) {
    return '"' + value.replace(/"/g, '""') + '"'
  }
  return value
}
